import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQ_ITEMS_UNFALLGUTACHTEN = [
  {
    question: 'Was kostet ein Unfallgutachten?',
    answer: 'Die Kosten für ein Unfallgutachten variieren je nach Schadensumfang und Fahrzeugtyp. Bei unverschuldetem Unfall trägt in der Regel die gegnerische Haftpflichtversicherung die Kosten – je nach Einzelfall.'
  },
  {
    question: 'Wer bezahlt das Gutachten bei unverschuldetem Unfall?',
    answer: 'In den meisten Fällen übernimmt die Haftpflichtversicherung des Unfallverursachers die Gutachterkosten. Als Geschädigter haben Sie das Recht auf freie Gutachterwahl und müssen meist nicht in Vorleistung gehen.'
  },
  {
    question: 'Wie schnell bekomme ich einen Termin?',
    answer: 'Wir vereinbaren meist innerhalb von 24–48 Stunden einen Termin vor Ort. Die Begutachtung dauert etwa 30–60 Minuten. Das fertige Gutachten erhalten Sie in der Regel innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Kommt ihr wirklich bundesweit?',
    answer: 'Ja, wir sind deutschlandweit im Einsatz – egal ob Hamburg, NRW, Bayern oder Berlin. Sie müssen nicht zu uns fahren, wir kommen zu Ihnen.'
  },
  {
    question: 'Muss ich zu euch kommen?',
    answer: 'Nein, wir kommen direkt zu Ihnen vor Ort – bundesweit. Sie müssen nicht zu uns fahren. Wir vereinbaren einen Termin an einem für Sie passenden Ort.'
  },
  {
    question: 'Was ist der Unterschied zwischen Gutachten und Kostenvoranschlag?',
    answer: 'Ein Gutachten ist eine umfassende, rechtlich belastbare Schadensdokumentation durch einen unabhängigen Sachverständigen. Ein Kostenvoranschlag ist eine oberflächliche Schätzung durch eine Werkstatt. Ab ca. 750€ Schadensumfang ist ein Gutachten empfehlenswert.'
  },
  {
    question: 'Was passiert, wenn das Auto nicht fahrbereit ist?',
    answer: 'Kein Problem. Wir begutachten das Fahrzeug dort, wo es sich befindet – bei Ihnen zu Hause, in einer Werkstatt oder an einem anderen Standort. Teilen Sie uns einfach mit, wo sich das Fahrzeug befindet.'
  },
  {
    question: 'Welche Unterlagen benötigt ihr?',
    answer: 'Idealerweise: Fahrzeugschein/-brief, Unfallbericht (falls vorhanden), Fotos vom Unfallort und Kontaktdaten der Unfallbeteiligten. Aber keine Sorge – wir klären das im Gespräch und helfen Ihnen weiter.'
  },
  {
    question: 'Kann ich das Gutachten auch für meine Versicherung nutzen?',
    answer: 'Ja, das Gutachten ist eine objektive Dokumentation und kann bei jeder Versicherung zur Schadenregulierung eingereicht werden. Es dient als Beweismittel und Grundlage für die Schadensabwicklung.'
  },
  {
    question: 'Was ist mit Wertminderung und Nutzungsausfall?',
    answer: 'Beides wird im Gutachten berücksichtigt. Die Wertminderung beschreibt den Wertverlust des Fahrzeugs durch den Unfall. Der Nutzungsausfall entschädigt Sie für die Zeit, in der Sie Ihr Fahrzeug nicht nutzen konnten.'
  },
  {
    question: 'Wie lange dauert die Begutachtung?',
    answer: 'Die Vor-Ort-Begutachtung dauert in der Regel 30–60 Minuten. Das fertige, schriftliche Gutachten erhalten Sie dann innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Was passiert nach der Begutachtung?',
    answer: 'Nach der Begutachtung erstellen wir das schriftliche Gutachten und senden es Ihnen zu. Sie können es dann bei der Versicherung einreichen. Bei Fragen zur weiteren Schadenregulierung unterstützen wir Sie gerne.'
  }
];

export const FAQUnfallgutachten = () => {
  return (
    <section className="py-28 lg:py-36 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/8 text-brand-green-dark rounded-full text-sm font-semibold mb-6 border border-brand-green/15">
            FAQ
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Häufig gestellte Fragen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-normal">
            Alles Wichtige rund um Unfallgutachten auf einen Blick
          </p>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-10 lg:p-16 shadow-modern-lg">
          <Accordion type="single" collapsible className="w-full space-y-5">
            {FAQ_ITEMS_UNFALLGUTACHTEN.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`} 
                className="border border-gray-200 rounded-2xl px-7 py-1 data-[state=open]:border-brand-green/40 data-[state=open]:shadow-xl transition-all bg-white"
              >
                <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-brand-green py-6 hover:no-underline text-base lg:text-lg">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-6 h-6 text-brand-green flex-shrink-0 mt-0.5" />
                    <span className="pr-4">{item.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-6 pl-10 text-[15px]">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-10 text-center">
          <a href="/nach-dem-unfall" className="text-brand-green hover:text-brand-green-dark font-semibold text-lg transition-colors">
            Was tun nach einem Unfall? Zur Checkliste →
          </a>
        </div>
      </div>
    </section>
  );
};